// petconnect-v2/frontend/src/components/common/Sidebar.js
// UPDATED: Added 🤖 AI Photo Search nav item
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, LogIn } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useMessages } from '../../hooks/useMessages';

const Sidebar = ({ onProfileClick }) => {
  const { user, logout, isAnonymous } = useAuth();
  const { unreadCount } = useMessages();
  const location = useLocation();

  const CATEGORIES = [
    { id: '/',              name: 'Home Feed',        icon: '🏠' },
    { id: '/search',        name: 'Search',           icon: '🔍' },
    { id: '/visual-search', name: 'AI Photo Search',  icon: '🤖' }, // NEW
    { id: '/messages',      name: 'Messages',         icon: '💬', requiresAuth: true },
    { id: '/friends',       name: 'Friends',          icon: '👥', requiresAuth: true },
    { id: '/petwalkers',    name: 'Pet Walkers',      icon: '🚶' },
    { id: '/adoption',      name: 'Adoption',         icon: '❤️' },
    { id: '/missing',       name: 'Missing',          icon: '🔴' },
    { id: '/found',         name: 'Found',            icon: '🟢' },
    { id: '/success',       name: 'Success Stories',  icon: '⭐' },
    { id: '/map',           name: 'Map & Dual View',  icon: '🗺️' },
    { id: '/about',         name: 'About Us',         icon: 'ℹ️' },
  ];

  return (
    <aside className="hidden lg:flex flex-col w-64 bg-white shadow-xl fixed left-0 top-0 h-screen overflow-y-auto border-r border-gray-200 z-50">
      <div className="p-6">
        {/* Logo */}
        <Link to="/" className="block mb-6">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            🐾 PetConnectAI
          </h1>
        </Link>

        {/* User info */}
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
          {isAnonymous ? (
            <div className="flex items-center gap-2 flex-1">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-lg flex-shrink-0">
                👤
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-700 text-sm">Guest User</p>
                <Link to="/login" className="text-xs text-purple-600 hover:underline font-medium">
                  Sign in for full access →
                </Link>
              </div>
            </div>
          ) : (
            <button
              onClick={onProfileClick}
              className="flex items-center gap-2 flex-1 hover:bg-gray-100 rounded-lg p-2 transition"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                {user?.username?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="font-semibold text-gray-800 text-sm truncate">
                  {user?.username || 'User'}
                </p>
                <p className="text-xs text-gray-500">View Profile</p>
              </div>
            </button>
          )}
          <button
            onClick={logout}
            className="text-gray-400 hover:text-red-500 transition p-1 flex-shrink-0"
            title={isAnonymous ? 'Exit guest mode' : 'Logout'}
          >
            <LogOut size={18} />
          </button>
        </div>

        {/* Guest sign-in prompt */}
        {isAnonymous && (
          <Link
            to="/login"
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 mb-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl text-sm font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-sm"
          >
            <LogIn size={16} />
            Sign In / Create Account
          </Link>
        )}

        {/* Navigation */}
        <nav className="space-y-1">
          {CATEGORIES.map((cat) => {
            const isActive = location.pathname === cat.id;
            const isLocked = isAnonymous && cat.requiresAuth;

            return (
              <Link
                key={cat.id}
                to={cat.id}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition font-medium text-sm relative group ${
                  isActive
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                    : isLocked
                    ? 'text-gray-400 hover:bg-gray-50'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="text-xl flex-shrink-0">{cat.icon}</span>
                <span className="truncate flex-1">{cat.name}</span>

                {isLocked && (
                  <span className="text-xs text-gray-400 flex-shrink-0">🔒</span>
                )}

                {cat.id === '/messages' && unreadCount > 0 && !isAnonymous && (
                  <span className="absolute top-1 right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;